import {
  SOURCE_SMS,
  SOURCE_RCS,
  SOURCE_TCAPI,
  type LocationSources,
  SOURCE_ADMIN_SMS,
} from '@rsos/constants/locationSources';
import { TEXT } from '@rsos/constants/locationTypes';
import { RCS_CONVERSATION_STATUS_ACTIVE } from '@rsos/constants/rcsStatuses';
import useHasAccessToGoogleRCS from '@rsos/hooks/useHasAccessToGoogleRCS';
import {
  ChatComponentType,
  type KeyOfChatComponentType,
} from '../../JurisdictionView/useChatComponents/typings';
import {
  PhoneDetails,
  RegularNewMessage,
  TextMessage,
} from './EmergencyCall.styles';

const TEXT_SOURCES = new Set([
  SOURCE_RCS,
  SOURCE_SMS,
  SOURCE_TCAPI,
  SOURCE_ADMIN_SMS,
]);

interface TextMessageStatusProps {
  isTT911?: boolean;
  source?: LocationSources;
  classOfServiceCategory?: string;
  hasNewMessage?: boolean;
  unreadRcsMessage?: boolean;
  rcsConversationStatus?: number;
  requestedLocationReceived?: boolean;
  tt911Message?: string;
  rcsStatusText?: string;
  hasAgent511Access?: boolean;
  isOutboundSMS?: boolean;
  primaryChatKey?: KeyOfChatComponentType;
}

const TextMessageStatus = ({
  isTT911,
  source,
  classOfServiceCategory,
  hasNewMessage,
  unreadRcsMessage,
  rcsConversationStatus,
  requestedLocationReceived,
  tt911Message,
  rcsStatusText,
  hasAgent511Access,
  isOutboundSMS,
  primaryChatKey,
}: TextMessageStatusProps) => {
  const hasGoogleRCSAccess = useHasAccessToGoogleRCS();

  const isTextSource =
    (source && TEXT_SOURCES.has(source)) ||
    isTT911 ||
    classOfServiceCategory === TEXT ||
    isOutboundSMS;

  const shouldShowAgentMessage = hasAgent511Access && tt911Message;
  const shouldShowRCSStatus = source === SOURCE_RCS && hasGoogleRCSAccess;

  const isPrimaryConversations =
    primaryChatKey === ChatComponentType.conversations;
  const isPrimaryTF911 = primaryChatKey === ChatComponentType.tf911;
  const isPrimaryRCS = primaryChatKey === ChatComponentType.rcs;

  if (requestedLocationReceived) {
    return <RegularNewMessage>Location Received</RegularNewMessage>;
  }
  if (hasNewMessage) {
    return <RegularNewMessage>NEW MESSAGE</RegularNewMessage>;
  }
  if (
    unreadRcsMessage &&
    rcsConversationStatus === RCS_CONVERSATION_STATUS_ACTIVE
  ) {
    return <RegularNewMessage>NEW RCS MESSAGE</RegularNewMessage>;
  }

  // Text type labels only render for text-based sources with no unread badges
  if (!isTextSource) {
    return null;
  }

  // Agent message is ungated — highest priority regardless of bucket
  if (shouldShowAgentMessage) {
    return <TextMessage>{tt911Message}</TextMessage>;
  }

  // RCS status: requires both the existing check AND rcs being primary bucket
  if (shouldShowRCSStatus && isPrimaryRCS) {
    return <PhoneDetails>{rcsStatusText}</PhoneDetails>;
  }

  // Admin SMS is a conversations subtype — only show when conversations is primary
  if (source === SOURCE_ADMIN_SMS && isPrimaryConversations) {
    return <PhoneDetails>ADMIN LINE TEXT</PhoneDetails>;
  }

  // Outbound 911 text belongs to TF911 — only show when TF911 is primary
  if (isOutboundSMS && isPrimaryTF911) {
    return <TextMessage>OUTBOUND 911 TEXT</TextMessage>;
  }

  return <PhoneDetails>Text-to-911</PhoneDetails>;
};

export default TextMessageStatus;
